import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Lo que se ve cuando no hay nada que ver.
 *
 * Existe porque habia 51 mensajes de «no hay nada» escritos a mano, cada uno con
 * su redaccion y su forma. Sale del patron que ya usaba `pages/AgentsPage.tsx`.
 *
 * Es de las pocas cajas que el criterio de composicion justifica
 * (harness/skills/muveran-design/SKILL.md): un borde se gana cuando aisla algo
 * excepcional, y esto lo es. Discontinuo a proposito, para que no se lea como
 * contenido.
 *
 *   <EmptyState
 *     icon={<Bot className="h-6 w-6" />}
 *     title="No hay agentes para mostrar"
 *     description="Crea tu primer agente con el flujo guiado."
 *   >
 *     <Button onClick={crear}>Crear agente</Button>
 *   </EmptyState>
 *
 * La descripcion deberia decir **que hacer**, no repetir que esta vacio. Y si se
 * llego aqui por un filtro, decirlo: no es lo mismo «no hay» que «no hay con
 * esto».
 */
interface EmptyStateProps extends Omit<React.ComponentProps<"div">, "title"> {
  title: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, title, description, icon, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-dashed bg-muted/30 p-10 text-center",
        className,
      )}
      {...props}
    >
      {icon ? (
        <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
      ) : null}
      <h2 className={cn("text-lg font-semibold", icon && "mt-3")}>{title}</h2>
      {description ? (
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          {description}
        </p>
      ) : null}
      {children ? <div className="mt-4">{children}</div> : null}
    </div>
  ),
)
EmptyState.displayName = "EmptyState"

export { EmptyState }
